import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useLayoutTier } from '../../hooks/useLayoutTier';
import { runViewTransition } from '../../utils/viewTransition';

/** Every layout, in the order a tablet cycles them. The first is its default. */
export const MACHINE_VIEWS = ['table', 'grid', 'list'] as const;

export type MachineView = (typeof MACHINE_VIEWS)[number];

/** A phone is too narrow for four columns, so it drops the table layout. */
const PHONE_VIEWS: readonly MachineView[] = ['list', 'grid'];

export interface MachineViewMode {
  view: MachineView;
  /** The layouts this tier offers, in the order the toggle cycles them. */
  views: readonly MachineView[];
  setView: (view: MachineView) => void;
}

/**
 * The machines list layout, in the URL beside the filters. `replace` keeps view
 * toggles out of the history stack. A phone reads a narrower set than a tablet,
 * so a link written on a tablet lands on the list rather than on nothing.
 */
export function useMachineViewMode(): MachineViewMode {
  const { isPhone } = useLayoutTier();
  const [stored, setStored] = useQueryState(
    'view',
    parseAsStringLiteral(MACHINE_VIEWS).withOptions({ history: 'replace' })
  );

  const views = isPhone ? PHONE_VIEWS : MACHINE_VIEWS;
  const defaultView = views[0];
  const view = stored && views.includes(stored) ? stored : defaultView;

  // The default needs no parameter, so cycling back to it clears the URL. A view
  // transition cross-fades the old layout into the new one.
  const setView = useCallback(
    (next: MachineView) =>
      runViewTransition('layout', () => {
        flushSync(() => {
          void setStored(next === defaultView ? null : next);
        });
      }),
    [defaultView, setStored]
  );

  return { view, views, setView };
}
